/**
 * RevOps Module Page
 * Revenue operations, subscriptions, billing, and analytics
 * Integrated with Sales Pipeline for real data
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import { config } from '@/src/config';
import {
  DollarSign,
  CreditCard,
  TrendingUp,
  TrendingDown as TrendingDownIcon,
  Users,
  Calendar,
  BarChart3,
  PieChart,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Download,
  Plus,
  X,
  Edit,
  Eye,
  Loader2,
  Send,
  Building2,
  ArrowRight,
  Printer,
  Settings,
  Palette,
  Image,
  FileDown,
  Trash2,
  Copy,
  MapPin,
  Calculator,
  CreditCard as BankIcon,
  Globe,
  Upload,
  ExternalLink,
  Gauge,
  Target,
  HeartHandshake,
} from 'lucide-react';
import { InvoiceTemplateSettings as SettingsTab } from '@/components/billing/InvoiceTemplateSettings';
import TierConfigurator from '@/src/platform/modules/revops/components/TierConfigurator';
import ConsumptionPricingTab from '@/src/platform/modules/revops/components/ConsumptionPricingTab';
import SubscriptionsTabConnected from '@/src/platform/modules/revops/components/SubscriptionsTabConnected';
import FeeOverridesEditor from '@/src/platform/modules/revops/components/FeeOverridesEditor';
import PlanAssignments from '@/src/platform/modules/revops/components/PlanAssignments';
import OrgBillingProfile from '@/src/platform/modules/revops/components/OrgBillingProfile';
import ClientUsageOverview from '@/src/platform/modules/revops/components/ClientUsageOverview';
import OutcomeBillingTab from '@/src/platform/modules/revops/components/OutcomeBillingTab';
import SponsorshipsTab from '@/src/platform/modules/revops/components/SponsorshipsTab';
import PriceBooksPanel from '@/src/platform/modules/revops/components/PriceBooksPanel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  useRevOps,
  RevOpsProvider,
  RevOpsSubscription,
  RevOpsInvoice,
  InvoiceLineItem,
  InvoiceTemplate,
  PipelineOpportunity,
  Organization,
  TaxConfiguration,
  InvoiceLineItemWithTax,
} from '@/contexts/RevOpsContext';

// ============================================================================
// TYPES
// ============================================================================

type RevOpsTab = 'overview' | 'subscriptions' | 'billing' | 'analytics' | 'settings' | 'pricing' | 'usage' | 'outcomes' | 'sponsorships';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ============================================================================
// INVOICE PREVIEW COMPONENT
// ============================================================================

interface InvoicePreviewProps {
  invoice: RevOpsInvoice;
  template: InvoiceTemplate;
  organization?: Organization;
}

function InvoicePreview({ invoice, template, organization }: InvoicePreviewProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm" style={{ minWidth: '600px' }}>
      {/* Header with branding */}
      <div className="p-6 border-b border-gray-200" style={{ backgroundColor: template.primaryColor + '10' }}>
        <div className="flex justify-between items-start">
          <div>
            {template.showLogo && template.logoUrl ? (
              <img src={template.logoUrl} alt="Company Logo" className="h-12 mb-2" />
            ) : (
              <h2 className="text-2xl font-bold" style={{ color: template.primaryColor }}>
                {template.companyName}
              </h2>
            )}
            {template.companyAddress && (
              <p className="text-sm text-gray-600 whitespace-pre-line mt-1">{template.companyAddress}</p>
            )}
            {template.companyEmail && <p className="text-sm text-gray-600">{template.companyEmail}</p>}
            {template.companyPhone && <p className="text-sm text-gray-600">{template.companyPhone}</p>}
          </div>
          <div className="text-right">
            <h1 className="text-3xl font-bold" style={{ color: template.primaryColor }}>INVOICE</h1>
            <p className="text-lg font-semibold text-gray-800 mt-2">{invoice.invoiceNumber}</p>
            <div className="mt-2 text-sm text-gray-600">
              <p>Date: {formatDate(invoice.issuedDate)}</p>
              <p>Due: {formatDate(invoice.dueDate)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To</h3>
            <p className="font-semibold text-gray-900">{invoice.customer}</p>
            {organization?.billing_address && (
              <p className="text-sm text-gray-600 whitespace-pre-line">{organization.billing_address}</p>
            )}
            {organization?.billing_email && (
              <p className="text-sm text-gray-600">{organization.billing_email}</p>
            )}
          </div>
          <div className="text-right">
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
              invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
              invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {invoice.status.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="p-6">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 text-sm font-semibold text-gray-600">Description</th>
              <th className="text-center py-2 text-sm font-semibold text-gray-600">Qty</th>
              <th className="text-right py-2 text-sm font-semibold text-gray-600">Unit Price</th>
              <th className="text-right py-2 text-sm font-semibold text-gray-600">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems?.map((item, idx) => (
              <tr key={item.id || idx} className="border-b border-gray-100">
                <td className="py-3 text-sm text-gray-900">{item.description}</td>
                <td className="py-3 text-sm text-gray-600 text-center">{item.quantity}</td>
                <td className="py-3 text-sm text-gray-600 text-right">{formatCurrency(item.unitPrice)}</td>
                <td className="py-3 text-sm font-medium text-gray-900 text-right">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-6 flex justify-end">
          <div className="w-64">
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-600">Subtotal</span>
              <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.tax && invoice.tax > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Tax</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.tax)}</span>
              </div>
            )}
            {invoice.discount && invoice.discount > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Discount</span>
                <span className="text-sm font-medium text-green-600">-{formatCurrency(invoice.discount)}</span>
              </div>
            )}
            <div className="flex justify-between py-3 border-t border-gray-200 mt-2">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-lg font-bold" style={{ color: template.primaryColor }}>
                {formatCurrency(invoice.amount)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes & Terms */}
      {(invoice.notes || template.paymentInstructions || template.termsAndConditions) && (
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          {invoice.notes && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-600 mb-1">Notes</h4>
              <p className="text-sm text-gray-700">{invoice.notes}</p>
            </div>
          )}
          {template.showPaymentInstructions && template.paymentInstructions && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-600 mb-1">Payment Instructions</h4>
              <p className="text-sm text-gray-700">{template.paymentInstructions}</p>
            </div>
          )}
          {template.termsAndConditions && (
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-1">Terms & Conditions</h4>
              <p className="text-sm text-gray-500">{template.termsAndConditions}</p>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      {template.footerText && (
        <div className="p-4 text-center border-t border-gray-200">
          <p className="text-sm text-gray-500">{template.footerText}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MODAL COMPONENTS
// ============================================================================


// ============================================================================
// CREATE INVOICE MODAL WITH BRANDING
// ============================================================================

interface CreateInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefilledCustomer?: string;
  prefilledCustomerId?: number;
  prefilledAmount?: number;
  prefilledLineItems?: InvoiceLineItem[];
  subscriptionId?: string;
}

function CreateInvoiceModal({
  isOpen,
  onClose,
  prefilledCustomer,
  prefilledCustomerId,
  prefilledAmount,
  prefilledLineItems,
  subscriptionId,
}: CreateInvoiceModalProps) {
  const {
    organizations,
    invoiceTemplates,
    createInvoice,
  } = useRevOps();

  const [step, setStep] = useState<'details' | 'items' | 'preview'>('details');
  const [customer, setCustomer] = useState(prefilledCustomer || '');
  const [customerId, setCustomerId] = useState<number | undefined>(prefilledCustomerId);
  const [dueDate, setDueDate] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(invoiceTemplates.find(t => t.isDefault)?.id || '');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>(prefilledLineItems || []);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (prefilledCustomer) setCustomer(prefilledCustomer);
    if (prefilledCustomerId) setCustomerId(prefilledCustomerId);
    if (prefilledLineItems) setLineItems(prefilledLineItems);

    // Default due date to 30 days from now
    const defaultDue = new Date();
    defaultDue.setDate(defaultDue.getDate() + 30);
    setDueDate(defaultDue.toISOString().split('T')[0]);
  }, [prefilledCustomer, prefilledCustomerId, prefilledLineItems]);

  const selectedTemplate = invoiceTemplates.find(t => t.id === selectedTemplateId) || invoiceTemplates[0];
  const selectedOrganization = organizations.find(o => o.id === customerId);

  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const total = subtotal;

  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: `item_${Date.now()}`,
        description: '',
        quantity: 1,
        unitPrice: 0,
        amount: 0,
      },
    ]);
  };

  const handleUpdateLineItem = (index: number, updates: Partial<InvoiceLineItem>) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], ...updates };
    if (updates.quantity !== undefined || updates.unitPrice !== undefined) {
      newItems[index].amount = newItems[index].quantity * newItems[index].unitPrice;
    }
    setLineItems(newItems);
  };

  const handleRemoveLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleCustomerChange = (orgId: number) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setCustomer(org.name);
      setCustomerId(orgId);
    }
  };

  const handleSubmit = () => {
    const invoice = createInvoice({
      customer,
      customerId,
      subscriptionId,
      amount: total,
      subtotal,
      status: 'draft',
      dueDate,
      issuedDate: new Date().toISOString().split('T')[0],
      lineItems,
      notes,
      template: selectedTemplate,
    });

    // Reset form
    setCustomer('');
    setCustomerId(undefined);
    setDueDate('');
    setNotes('');
    setLineItems([]);
    setStep('details');
    onClose();
  };

  // Preview invoice object
  const previewInvoice: RevOpsInvoice = {
    id: 'preview',
    invoiceNumber: 'INV-PREVIEW',
    customer,
    customerId,
    amount: total,
    subtotal,
    status: 'draft',
    dueDate,
    issuedDate: new Date().toISOString().split('T')[0],
    lineItems,
    notes,
    template: selectedTemplate,
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${showPreview ? 'sm:max-w-[900px]' : 'sm:max-w-[600px]'}`}>
        <DialogHeader>
          <DialogTitle>Create Invoice</DialogTitle>
          <DialogDescription>
            {step === 'details' && 'Enter invoice details and select a template'}
            {step === 'items' && 'Add line items to the invoice'}
            {step === 'preview' && 'Review and submit the invoice'}
          </DialogDescription>
        </DialogHeader>

        <div className={`py-4 ${showPreview ? 'flex gap-6' : ''}`}>
          {/* Form Section */}
          <div className={showPreview ? 'w-1/2' : 'w-full'}>
            {/* Step Indicator */}
            <div className="flex items-center gap-2 mb-6">
              {['details', 'items', 'preview'].map((s, idx) => (
                <React.Fragment key={s}>
                  <button
                    onClick={() => setStep(s as typeof step)}
                    className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                      step === s
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                      step === s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {idx + 1}
                    </span>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                  {idx < 2 && <ArrowRight className="h-4 w-4 text-gray-300" />}
                </React.Fragment>
              ))}
            </div>

            {step === 'details' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    value={customerId || ''}
                    onChange={(e) => handleCustomerChange(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select organization...</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Template</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {invoiceTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(template.id)}
                        className={`p-3 border rounded-lg text-left ${
                          selectedTemplateId === template.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: template.primaryColor }}
                          />
                          <span className="text-sm font-medium text-gray-900">{template.name}</span>
                        </div>
                        {template.isDefault && (
                          <span className="text-xs text-blue-600">Default</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Additional notes for this invoice..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep('items')}
                    disabled={!customer || !dueDate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Next: Add Items
                  </button>
                </div>
              </div>
            )}

            {step === 'items' && (
              <div className="space-y-4">
                {/* Line Items */}
                <div className="space-y-2">
                  {lineItems.map((item, idx) => (
                    <div key={item.id} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleUpdateLineItem(idx, { description: e.target.value })}
                          placeholder="Description"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div className="w-20">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleUpdateLineItem(idx, { quantity: Number(e.target.value) })}
                          min="1"
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
                        />
                      </div>
                      <div className="w-28">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => handleUpdateLineItem(idx, { unitPrice: Number(e.target.value) })}
                            min="0"
                            step="0.01"
                            className="w-full pl-6 pr-2 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <div className="w-24 text-right py-2 font-medium text-sm">
                        {formatCurrency(item.amount)}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(idx)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleAddLineItem}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Plus className="h-4 w-4" />
                  Add Line Item
                </button>

                {/* Totals */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between mt-2 pt-2 border-t border-gray-200">
                    <span className="font-medium">Total</span>
                    <span className="text-lg font-bold text-blue-600">{formatCurrency(total)}</span>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setStep('details')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep('preview')}
                    disabled={lineItems.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Next: Preview
                  </button>
                </div>
              </div>
            )}

            {step === 'preview' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-800 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Ready to Create
                  </h4>
                  <div className="mt-2 text-sm text-green-700 space-y-1">
                    <p><strong>Customer:</strong> {customer}</p>
                    <p><strong>Amount:</strong> {formatCurrency(total)}</p>
                    <p><strong>Due Date:</strong> {formatDate(dueDate)}</p>
                    <p><strong>Template:</strong> {selectedTemplate?.name}</p>
                    <p><strong>Line Items:</strong> {lineItems.length}</p>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setStep('items')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Create Invoice
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="w-1/2 border-l pl-6 overflow-auto max-h-[500px]">
              <div className="transform scale-75 origin-top-left">
                <InvoicePreview
                  invoice={previewInvoice}
                  template={selectedTemplate}
                  organization={selectedOrganization}
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// VIEW INVOICE MODAL WITH DOWNLOAD
// ============================================================================

interface ViewInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: RevOpsInvoice | null;
  onSendReminder: () => void;
  onStatusChange: (status: RevOpsInvoice['status']) => void;
}

function ViewInvoiceModal({ isOpen, onClose, invoice, onSendReminder, onStatusChange }: ViewInvoiceModalProps) {
  const { invoiceTemplates, organizations } = useRevOps();
  const printRef = useRef<HTMLDivElement>(null);

  if (!invoice) return null;

  const template = invoice.template || invoiceTemplates.find(t => t.isDefault) || invoiceTemplates[0];
  const organization = organizations.find(o => o.id === invoice.customerId);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoice.invoiceNumber}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 20px; }
            * { box-sizing: border-box; }
          </style>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownloadPDF = () => {
    // In a real app, this would call a backend API to generate PDF
    // For now, we'll use the print functionality
    handlePrint();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Invoice {invoice.invoiceNumber}</DialogTitle>
              <DialogDescription>{invoice.customer}</DialogDescription>
            </div>
            <div className="flex gap-2">
              <select
                value={invoice.status}
                onChange={(e) => onStatusChange(e.target.value as RevOpsInvoice['status'])}
                className={`px-3 py-1 rounded-lg text-sm font-medium border ${
                  invoice.status === 'paid' ? 'bg-green-100 text-green-800 border-green-200' :
                  invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                  invoice.status === 'overdue' ? 'bg-red-100 text-red-800 border-red-200' :
                  'bg-gray-100 text-gray-800 border-gray-200'
                }`}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="void">Void</option>
              </select>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div ref={printRef}>
            <InvoicePreview
              invoice={invoice}
              template={template}
              organization={organization}
            />
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          {(invoice.status === 'pending' || invoice.status === 'overdue' || invoice.status === 'sent') && (
            <button
              onClick={onSendReminder}
              className="px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              Send Reminder
            </button>
          )}
          <button
            onClick={handlePrint}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
          >
            <FileDown className="h-4 w-4" />
            Download PDF
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============================================================================
// TAB COMPONENTS
// ============================================================================

function OverviewTab() {
  const { subscriptions, invoices, wonDeals, accounts, getAnalytics } = useRevOps();
  const analytics = getAnalytics();

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Recurring Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(analytics.totalMrr)}</p>
              <div className="flex items-center gap-1 mt-2 text-green-600">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">From {analytics.activeSubscriptions} subscriptions</span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Annual Recurring Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(analytics.totalArr)}</p>
              <div className="flex items-center gap-1 mt-2 text-green-600">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">+{analytics.netRevenueRetention - 100}% NRR</span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Accounts</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.activeAccounts}</p>
              <div className="flex items-center gap-1 mt-2 text-gray-500">
                <Building2 className="h-4 w-4" />
                <span className="text-sm">{wonDeals.length} won deals</span>
              </div>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Invoices</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {formatCurrency(analytics.pendingInvoicesValue)}
              </p>
              <div className="flex items-center gap-1 mt-2 text-yellow-600">
                <Clock className="h-4 w-4" />
                <span className="text-sm">{invoices.filter(i => i.status === 'pending' || i.status === 'sent').length} invoices</span>
              </div>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FileText className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Revenue by Pricing Model</h4>
          {analytics.revenueByPricingModel.length > 0 ? (
            <div className="space-y-3">
              {analytics.revenueByPricingModel.map((item) => (
                <div key={item.model} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">{item.label}</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(item.mrr)} ({item.percentage}%)
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              <PieChart className="h-12 w-12 opacity-50" />
              <span className="ml-2">No revenue data yet</span>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Recent Won Deals</h4>
          {wonDeals.length > 0 ? (
            <div className="space-y-3">
              {wonDeals.slice(0, 5).map((deal) => (
                <div key={deal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{deal.company}</p>
                    <p className="text-sm text-gray-500">{deal.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{formatCurrency(deal.amount)}</p>
                    <p className="text-xs text-gray-500">{formatDate(deal.closeDate)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-500">
              <TrendingUp className="h-12 w-12 opacity-50 mb-2" />
              <p>No won deals yet</p>
              <p className="text-sm text-gray-400">Close deals in the Sales Pipeline</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Recent Invoices</h4>
        </div>
        <div className="divide-y divide-gray-200">
          {invoices.slice(0, 5).map((invoice) => (
            <div key={invoice.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  invoice.status === 'paid' ? 'bg-green-100' :
                  invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100' :
                  invoice.status === 'overdue' ? 'bg-red-100' :
                  'bg-gray-100'
                }`}>
                  <FileText className={`h-4 w-4 ${
                    invoice.status === 'paid' ? 'text-green-600' :
                    invoice.status === 'pending' || invoice.status === 'sent' ? 'text-yellow-600' :
                    invoice.status === 'overdue' ? 'text-red-600' :
                    'text-gray-600'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{invoice.customer}</p>
                  <p className="text-sm text-gray-500">{invoice.invoiceNumber}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">{formatCurrency(invoice.amount)}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                  invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
                  invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {invoice.status}
                </span>
              </div>
            </div>
          ))}
          {invoices.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No invoices yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function BillingTab() {
  const { invoices, subscriptions } = useRevOps();

  const collected = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const pending = invoices.filter(i => i.status === 'pending' || i.status === 'sent').reduce((sum, i) => sum + i.amount, 0);
  const overdue = invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0);

  return (
    <div className="space-y-6">
      {/* Billing Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">This Month</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Collected</span>
              <span className="font-medium text-green-600">{formatCurrency(collected)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Pending</span>
              <span className="font-medium text-yellow-600">{formatCurrency(pending)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Overdue</span>
              <span className="font-medium text-red-600">{formatCurrency(overdue)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Payment Methods</h4>
          <div className="space-y-3">
            {[
              { type: 'Credit Card', count: subscriptions.filter(s => s.status === 'active').length, icon: CreditCard },
              { type: 'ACH/Bank', count: 0, icon: DollarSign },
              { type: 'Invoice', count: invoices.length, icon: FileText },
            ].map((method) => (
              <div key={method.type} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <method.icon className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{method.type}</span>
                </div>
                <span className="font-medium text-gray-900">{method.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Failed Payments</h4>
          <div className="text-center py-4">
            <p className="text-3xl font-bold text-gray-900">
              {subscriptions.filter(s => s.status === 'past_due').length}
            </p>
            <p className="text-sm text-gray-500 mt-1">Requires attention</p>
          </div>
        </div>
      </div>

      {/* Payment Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-4">Billing Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Gateway</label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg">
              <option>Stripe</option>
              <option>PayPal</option>
              <option>Square</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Due Days</label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg">
              <option>Net 15</option>
              <option>Net 30</option>
              <option>Net 45</option>
              <option>Net 60</option>
            </select>
          </div>
        </div>
        <div className="mt-6">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

interface InvoicesTabProps {
  onCreateInvoice: () => void;
  onViewInvoice: (invoice: RevOpsInvoice) => void;
}

function InvoicesTab({ onCreateInvoice, onViewInvoice }: InvoicesTabProps) {
  const { invoices } = useRevOps();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredInvoices = statusFilter === 'all'
    ? invoices
    : invoices.filter(i => i.status === statusFilter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Invoices</h3>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <button
            onClick={onCreateInvoice}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Create Invoice
          </button>
        </div>
      </div>

      {filteredInvoices.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h4>
          <p className="text-gray-500 mb-4">
            Create your first invoice with custom branding and templates.
          </p>
          <button
            onClick={onCreateInvoice}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Invoice
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-medium text-blue-600">{invoice.invoiceNumber}</span>
                  </td>
                  <td className="px-6 py-4 text-gray-900">{invoice.customer}</td>
                  <td className="px-6 py-4 font-medium text-gray-900">{formatCurrency(invoice.amount)}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
                      invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{invoice.dueDate}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onViewInvoice(invoice)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="View"
                      >
                        <Eye className="h-4 w-4 text-gray-500" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 rounded" title="Download">
                        <Download className="h-4 w-4 text-blue-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AnalyticsTab() {
  const { getAnalytics, subscriptions, invoices, wonDeals } = useRevOps();
  const analytics = getAnalytics();

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Avg. Revenue per Customer</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(analytics.avgRevenuePerCustomer)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Customer LTV</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(analytics.customerLTV)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Net Revenue Retention</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.netRevenueRetention}%</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Churn Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.churnRate}%</p>
        </div>
      </div>

      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <p className="text-blue-100 text-sm">Monthly Recurring Revenue</p>
          <p className="text-3xl font-bold mt-2">{formatCurrency(analytics.totalMrr)}</p>
          <p className="text-blue-100 text-sm mt-2">{analytics.activeSubscriptions} active subscriptions</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <p className="text-green-100 text-sm">Annual Recurring Revenue</p>
          <p className="text-3xl font-bold mt-2">{formatCurrency(analytics.totalArr)}</p>
          <p className="text-green-100 text-sm mt-2">Projected for current year</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <p className="text-purple-100 text-sm">Won Deals Value</p>
          <p className="text-3xl font-bold mt-2">{formatCurrency(analytics.wonDealsValue)}</p>
          <p className="text-purple-100 text-sm mt-2">{wonDeals.length} won deals from pipeline</p>
        </div>
      </div>

      {/* Revenue by Pricing Model */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <PieChart className="h-5 w-5 text-gray-400" />
          Revenue Breakdown by Pricing Model
        </h4>
        {analytics.revenueByPricingModel.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              {analytics.revenueByPricingModel.map((item) => (
                <div key={item.model} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">{item.label}</span>
                    <span className="font-medium text-gray-900">{formatCurrency(item.mrr)} ({item.percentage}%)</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h5 className="font-medium text-gray-800">Pricing Mix Insights</h5>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Recurring Revenue</span>
                  <span className="font-medium text-gray-900">{formatCurrency(Math.round(analytics.totalMrr * 0.80))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Variable Revenue (Est.)</span>
                  <span className="font-medium text-gray-900">{formatCurrency(Math.round(analytics.totalMrr * 0.20))}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <PieChart className="h-12 w-12 mx-auto opacity-50 mb-2" />
            <p>No revenue data yet</p>
            <p className="text-sm text-gray-400">Create subscriptions to see analytics</p>
          </div>
        )}
      </div>

      {/* MRR Movements */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-gray-400" />
          MRR Waterfall - This Month
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {analytics.mrrMovements.map((movement) => {
            const isPositive = movement.amount >= 0;
            const bgColor = movement.type === 'new' ? 'bg-green-50 border-green-200' :
                           movement.type === 'expansion' ? 'bg-blue-50 border-blue-200' :
                           movement.type === 'reactivation' ? 'bg-teal-50 border-teal-200' :
                           movement.type === 'contraction' ? 'bg-orange-50 border-orange-200' :
                           'bg-red-50 border-red-200';
            const textColor = isPositive ? 'text-green-700' : 'text-red-700';

            return (
              <div key={movement.type} className={`text-center p-4 rounded-lg border ${bgColor}`}>
                <p className="text-xs uppercase tracking-wide text-gray-600">{movement.label}</p>
                <p className={`text-xl font-bold mt-2 ${textColor}`}>
                  {isPositive ? '+' : ''}{formatCurrency(movement.amount)}
                </p>
                <p className="text-xs text-gray-500 mt-1">{movement.count} customer{movement.count !== 1 ? 's' : ''}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Revenue Forecast */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-gray-400" />
          6-Month Revenue Forecast
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Month</th>
                <th className="text-right py-3 px-4 text-gray-600 font-medium">Conservative</th>
                <th className="text-right py-3 px-4 text-gray-600 font-medium">Projected</th>
                <th className="text-right py-3 px-4 text-gray-600 font-medium">Optimistic</th>
              </tr>
            </thead>
            <tbody>
              {analytics.forecast.map((forecast) => (
                <tr key={forecast.month} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{forecast.month} 2026</td>
                  <td className="py-3 px-4 text-right text-orange-600">{formatCurrency(forecast.conservative)}</td>
                  <td className="py-3 px-4 text-right font-medium text-gray-900">{formatCurrency(forecast.projected)}</td>
                  <td className="py-3 px-4 text-right text-green-600">{formatCurrency(forecast.optimistic)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50">
                <td className="py-3 px-4 font-medium text-gray-900">6-Month Total</td>
                <td className="py-3 px-4 text-right text-orange-600 font-medium">
                  {formatCurrency(analytics.forecast.reduce((sum, f) => sum + f.conservative, 0))}
                </td>
                <td className="py-3 px-4 text-right font-bold text-gray-900">
                  {formatCurrency(analytics.forecast.reduce((sum, f) => sum + f.projected, 0))}
                </td>
                <td className="py-3 px-4 text-right text-green-600 font-medium">
                  {formatCurrency(analytics.forecast.reduce((sum, f) => sum + f.optimistic, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SETTINGS TAB - TEMPLATE MANAGEMENT
// (TemplateEditorModal and SettingsTab extracted to shared components:
//  src/components/billing/TemplateEditorModal.tsx
//  src/components/billing/InvoiceTemplateSettings.tsx)
// ============================================================================

/* Local TemplateEditorModal and SettingsTab removed — imported from shared components above */

// ============================================================================
// FULL PAGE INVOICE VIEW
// ============================================================================

interface FullPageInvoiceViewProps {
  invoice: RevOpsInvoice;
  onClose: () => void;
  onStatusChange: (status: RevOpsInvoice['status']) => void;
  onEdit: () => void;
}

function FullPageInvoiceView({ invoice, onClose, onStatusChange, onEdit }: FullPageInvoiceViewProps) {
  const { invoiceTemplates, organizations } = useRevOps();
  const printRef = useRef<HTMLDivElement>(null);

  const template = invoice.template || invoiceTemplates.find(t => t.isDefault) || invoiceTemplates[0];
  const organization = organizations.find(o => o.id === invoice.customerId);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoice.invoiceNumber}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 40px; }
            * { box-sizing: border-box; }
            @media print { body { padding: 0; } }
          </style>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 overflow-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 shadow-sm z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
                <p className="text-sm text-gray-500">{invoice.customer}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <select
                value={invoice.status}
                onChange={(e) => onStatusChange(e.target.value as RevOpsInvoice['status'])}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                  invoice.status === 'paid' ? 'bg-green-100 text-green-800 border-green-200' :
                  invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                  invoice.status === 'overdue' ? 'bg-red-100 text-red-800 border-red-200' :
                  'bg-gray-100 text-gray-800 border-gray-200'
                }`}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="void">Void</option>
              </select>

              <button
                onClick={onEdit}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Edit className="h-4 w-4" />
                Edit
              </button>

              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Printer className="h-4 w-4" />
                Print
              </button>

              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FileDown className="h-4 w-4" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="max-w-4xl mx-auto py-8 px-6">
        <div ref={printRef} className="bg-white rounded-lg shadow-lg">
          <InvoicePreview
            invoice={invoice}
            template={template}
            organization={organization}
          />
        </div>

        {/* Invoice Meta Information */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4">INVOICE DETAILS</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-medium">{formatDate(invoice.issuedDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Due Date</p>
              <p className="font-medium">{formatDate(invoice.dueDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Amount</p>
              <p className="font-medium">{formatCurrency(invoice.amount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                invoice.status === 'pending' || invoice.status === 'sent' ? 'bg-yellow-100 text-yellow-800' :
                invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function RevOpsPageContent() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const {
    subscriptions,
    invoices,
    wonDeals,
    organizations,
    invoiceTemplates,
    isLoading,
    updateSubscription,
    updateInvoice,
    generateInvoiceFromSubscription,
  } = useRevOps();

  const [activeTab, setActiveTab] = useState<RevOpsTab>('overview');
  const [pricingSubTab, setPricingSubTab] = useState<'plans' | 'consumption' | 'fees'>('plans');

  // Modal states
  const [manageSubscriptionOpen, setManageSubscriptionOpen] = useState(false);
  const [createSubscriptionOpen, setCreateSubscriptionOpen] = useState(false);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [viewInvoiceOpen, setViewInvoiceOpen] = useState(false);
  const [convertDealOpen, setConvertDealOpen] = useState(false);

  // Selected items
  const [selectedSubscription, setSelectedSubscription] = useState<RevOpsSubscription | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<RevOpsInvoice | null>(null);

  // Invoice prefill data
  const [invoicePrefill, setInvoicePrefill] = useState<{
    customer?: string;
    customerId?: number;
    amount?: number;
    lineItems?: InvoiceLineItem[];
    subscriptionId?: string;
  }>({});

  const tabs: { id: RevOpsTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('platform.revOps.tabs.overview', 'Overview'), icon: <BarChart3 className="h-4 w-4" /> },
    // Subscriptions tab deprecated — superseded by Pricing → Plans & Assignments
    // (organization_tier_assignments) + the usage→org_invoices billing flow. The
    // legacy ro_subscriptions create modal 500s and duplicates assignments.
    // { id: 'subscriptions', label: t('platform.revOps.tabs.subscriptions', 'Subscriptions'), icon: <RefreshCw className="h-4 w-4" /> },
    { id: 'billing', label: t('platform.revOps.tabs.billing', 'Billing'), icon: <CreditCard className="h-4 w-4" /> },
    { id: 'analytics', label: t('platform.revOps.tabs.analytics', 'Analytics'), icon: <TrendingUp className="h-4 w-4" /> },
    { id: 'settings', label: t('platform.revOps.tabs.settings', 'Settings'), icon: <Settings className="h-4 w-4" /> },
    { id: 'pricing', label: 'Pricing', icon: <Calculator className="h-4 w-4" /> },
    { id: 'usage', label: 'Usage', icon: <Gauge className="h-4 w-4" /> },
    { id: 'outcomes', label: 'Outcomes', icon: <Target className="h-4 w-4" /> },
    { id: 'sponsorships', label: 'Sponsorships', icon: <HeartHandshake className="h-4 w-4" /> },
  ];

  // Full page invoice view state
  const [fullPageInvoiceView, setFullPageInvoiceView] = useState(false);

  // Handlers
  const handleManageSubscription = (sub: RevOpsSubscription) => {
    setSelectedSubscription(sub);
    setManageSubscriptionOpen(true);
  };

  const handleUpdateSubscription = (updatedSub: RevOpsSubscription) => {
    updateSubscription(updatedSub.id, updatedSub);
  };

  const handleGenerateInvoiceFromSubscription = (subscription: RevOpsSubscription) => {
    const lineItems: InvoiceLineItem[] = subscription.lineItems?.map(item => ({
      id: `ili_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      description: item.itemName,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      amount: item.monthlyRecurring,
    })) || [{
      id: `ili_${Date.now()}`,
      description: `${subscription.plan} - Monthly Subscription`,
      quantity: 1,
      unitPrice: subscription.mrr,
      amount: subscription.mrr,
    }];

    setInvoicePrefill({
      customer: subscription.customer,
      customerId: subscription.customerId,
      amount: subscription.mrr,
      lineItems,
      subscriptionId: subscription.id,
    });
    setManageSubscriptionOpen(false);
    setCreateInvoiceOpen(true);
  };

  const handleViewInvoice = (invoice: RevOpsInvoice, fullPage: boolean = false) => {
    setSelectedInvoice(invoice);
    if (fullPage) {
      setFullPageInvoiceView(true);
    } else {
      setViewInvoiceOpen(true);
    }
  };

  const handleInvoiceStatusChange = (status: RevOpsInvoice['status']) => {
    if (selectedInvoice) {
      updateInvoice(selectedInvoice.id, { status });
      setSelectedInvoice({ ...selectedInvoice, status });
    }
  };

  // Get existing deal IDs that have subscriptions
  const existingSubscriptionDealIds = subscriptions
    .filter(s => s.opportunityId)
    .map(s => s.opportunityId as string);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    // Triveny.ai: .triveny-platform activates the design-system overlay.
    // WeaveIn users see no change.
    <div className={`p-6 max-w-7xl mx-auto${config.isTriveny ? ' triveny-platform' : ''}`}>
      {/* Header — hidden for Triveny; the TvPillarBar already shows "OPERATE · RevOps". */}
      {!config.isTriveny && (
        <div className="mb-8">
          {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><DollarSign className="h-6 w-6 text-blue-600" />{t('platform.revOps.title', 'Revenue Operations')}</h1>}
          <p className="text-gray-600 mt-1">
            {t('platform.revOps.description', 'Manage subscriptions, billing, and revenue analytics - integrated with your Sales Pipeline')}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'subscriptions' && <SubscriptionsTabConnected />}
      {activeTab === 'billing' && <BillingTab />}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'settings' && <SettingsTab />}
      {activeTab === 'pricing' && (
        <div className="space-y-6">
          {/* Pricing sub-tabs */}
          <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
            {([
              { id: 'plans', label: 'Plans & Assignments' },
              { id: 'consumption', label: 'Consumption & Tokens' },
              { id: 'fees', label: 'Transaction Fees' },
            ] as const).map((st) => (
              <button
                key={st.id}
                onClick={() => setPricingSubTab(st.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  pricingSubTab === st.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {pricingSubTab === 'plans' && (
            <div className="space-y-8">
              <OrgBillingProfile />

              <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
                <PriceBooksPanel />
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Plan Tiers</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Templates that combine base pricing with one of six pricing models
                      (flat fee, per-seat, tiered, usage-based, revenue share, hybrid).
                      Plan tiers are what RevOps bills against — assigned to an org at close-won.
                    </p>
                  </div>
                </div>
                <TierConfigurator />
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
                <PlanAssignments />
              </div>
            </div>
          )}

          {pricingSubTab === 'fees' && (
            <div>
              <FeeOverridesEditor />
            </div>
          )}

          {pricingSubTab === 'consumption' && (
            <div>
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Consumption &amp; Tokens</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Per-channel (SMS/email/voice) and per-token rates — set as a shared template
                  or assigned to a specific organization with an included allowance + overage.
                </p>
              </div>
              <ConsumptionPricingTab />
            </div>
          )}
        </div>
      )}

      {activeTab === 'usage' && <ClientUsageOverview />}
      {activeTab === 'outcomes' && <OutcomeBillingTab />}
      {activeTab === 'sponsorships' && <SponsorshipsTab />}

      {/* Full Page Invoice View */}
      {fullPageInvoiceView && selectedInvoice && (
        <FullPageInvoiceView
          invoice={selectedInvoice}
          onClose={() => {
            setFullPageInvoiceView(false);
            setSelectedInvoice(null);
          }}
          onStatusChange={handleInvoiceStatusChange}
          onEdit={() => {
            setFullPageInvoiceView(false);
            // Could open edit modal here
          }}
        />
      )}

      {/* Modals */}
      {/* Subscription create/manage/import now live in <SubscriptionsTabConnected/> (real API). */}
      <CreateInvoiceModal
        isOpen={createInvoiceOpen}
        onClose={() => {
          setCreateInvoiceOpen(false);
          setInvoicePrefill({});
        }}
        prefilledCustomer={invoicePrefill.customer}
        prefilledCustomerId={invoicePrefill.customerId}
        prefilledAmount={invoicePrefill.amount}
        prefilledLineItems={invoicePrefill.lineItems}
        subscriptionId={invoicePrefill.subscriptionId}
      />
      <ViewInvoiceModal
        isOpen={viewInvoiceOpen}
        onClose={() => {
          setViewInvoiceOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onSendReminder={() => {
          alert('Payment reminder sent!');
        }}
        onStatusChange={handleInvoiceStatusChange}
      />
    </div>
  );
}

export function RevOpsPage() {
  return (
    <RevOpsProvider>
      <RevOpsPageContent />
    </RevOpsProvider>
  );
}

export default RevOpsPage;
